import {alpha, SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {PHONE_BOTTOM_BAR_HEIGHT, PHONE_TOUCH} from 'src/components/mobile/mobileDesign.sx';

/** The app bar standing above this screen, which the menu has to clear to fill the rest of it. */
const APP_BAR_HEIGHT = 56;

/** What the shell pads a page with below `md`, and what this screen bleeds back out of. */
const SHELL_PADDING_X = 15;
const SHELL_PADDING_Y = 20;

/**
 * The menu surface.
 *
 * Bled out of the shell's padding on three sides: this is a menu, not a card on a page, and it
 * reaches every edge the way the drawer it stands in for does. The shell's own clearance for the
 * bottom bar is cancelled and re-added as padding inside, so the dark surface runs all the way under
 * the bar rather than stopping above a white strip.
 */
export const moreScreenSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(12 / 8),
    marginLeft: theme.spacing(-SHELL_PADDING_X / 8),
    marginRight: theme.spacing(-SHELL_PADDING_X / 8),
    marginTop: theme.spacing(-SHELL_PADDING_Y / 8),
    marginBottom: theme.spacing(-(SHELL_PADDING_Y + PHONE_BOTTOM_BAR_HEIGHT) / 8),
    paddingTop: theme.spacing(14 / 8),
    paddingBottom: theme.spacing((PHONE_BOTTOM_BAR_HEIGHT + 16) / 8),
    minHeight: `calc(100dvh - ${APP_BAR_HEIGHT}px)`,
    backgroundColor: theme.palette.neutral[900],
};

/** Who is looking, and which business they are looking at. */
export const identitySx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(12 / 8),
    padding: theme.spacing(0, 16 / 8),
};

export const avatarSx: SxProps<Theme> = {
    flexShrink: 0,
    width: PHONE_TOUCH,
    height: PHONE_TOUCH,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(theme.palette.common.white, 0.12),
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    '& .MuiTypography-root': {
        fontSize: '15px',
        fontWeight: 600,
        color: theme.palette.common.white,
    },
};

export const identityTextSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3 / 8),
    flexGrow: 1,
    minWidth: 0,
};

export const identityNameSx: SxProps<Theme> = {
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.2,
    color: theme.palette.common.white,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export const identityBusinessSx: SxProps<Theme> = {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.2,
    color: alpha(theme.palette.common.white, 0.55),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

/** Three to a row: everything the drawer lists, on one screen instead of a seventeen-row column. */
export const tileGridSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(10 / 8),
    padding: theme.spacing(0, 16 / 8),
};

/** One entry. Taller than the 44px floor, because the label under the icon needs two lines in German. */
export const tileSx = (destructive = false): SxProps<Theme> => ({
    position: 'relative',
    height: 80,
    minHeight: PHONE_TOUCH,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(8 / 8),
    padding: theme.spacing(0, 6 / 8),
    border: 'none',
    borderRadius: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textDecoration: 'none',
    textAlign: 'center',
    backgroundColor: destructive
        ? alpha(theme.palette.brand.main, 0.14)
        : alpha(theme.palette.common.white, 0.07),
    color: destructive ? theme.palette.brand.light : theme.palette.common.white,
    '& svg': {
        fontSize: '22px',
        fill: destructive ? theme.palette.brand.light : alpha(theme.palette.common.white, 0.85),
    },
    '& .MuiTypography-root': {
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: 1.25,
        textAlign: 'center',
        color: 'inherit',
    },
    '&:hover': {
        backgroundColor: destructive
            ? alpha(theme.palette.brand.main, 0.2)
            : alpha(theme.palette.common.white, 0.12),
    },
});

/** Where the unread count sits on the notifications tile. The marker itself is the drawer's. */
export const tileBadgeSx: SxProps<Theme> = {
    position: 'absolute',
    top: theme.spacing(6 / 8),
    right: theme.spacing(8 / 8),
};
